import axios from "axios";
import type { UserId } from "@/api/users";
import type { TicketId, TicketKey } from "@/api/tickets";
import type { ProjectId } from "@/api/projects";
import type { OrganizationId } from "@/api/organizations";

export type WorkLogId = string & { __brand: "WorkLogId" };

export type WorkLogTicketShare = {
    ticketId: TicketId;
    key: TicketKey;
    title: string;
    seconds: number;
};

export type WorkLog = {
    id: WorkLogId;
    started: Date;
    stopped: Date | null;
    text: string | null;
    projectId: ProjectId | null;
    projectName: string | null;
    organizationId: OrganizationId | null;
    organizationName: string | null;
    tickets: WorkLogTicketShare[];
};

export type WorkLogs = {
    entries: WorkLog[];
    /**
     * The server's clock at the time of the response. Elapsed time is rendered
     * against this rather than the browser's clock, which may be well off.
     */
    serverNow: Date;
    /** Site rule: a stopped entry must go against tickets, a project, or an organization. */
    requireTarget: boolean;
};

type WorkLogResponse = Omit<WorkLog, "started" | "stopped"> & {
    started: string;
    stopped: string | null;
};

function toWorkLog(entry: WorkLogResponse): WorkLog {
    return {
        ...entry,
        started: new Date(entry.started),
        stopped: entry.stopped === null ? null : new Date(entry.stopped),
    };
}

export type DeletedWorkLog = {
    id: WorkLogId;
    userId: UserId;
    started: Date;
    stopped: Date | null;
    deletedAt: Date | null;
    text: string | null;
    projectName: string | null;
    tickets: string[];
};

type DeletedWorkLogResponse = Omit<DeletedWorkLog, "started" | "stopped" | "deletedAt"> & {
    started: string;
    stopped: string | null;
    deletedAt: string | null;
};

/** A ticket's share of an entry. Seconds are ignored while the clock runs. */
export type WorkLogTicketAllocation = {
    ticketId: TicketId;
    seconds: number;
};

export type WorkLogEdit = {
    text: string | null;
    started: Date;
    stopped: Date | null;
    tickets: WorkLogTicketAllocation[];
    projectId: ProjectId | null;
    organizationId: OrganizationId | null;
};

export type TimeReviewLine = {
    id: string;
    label: string;
    detail: string | null;
    seconds: number;
};

export type TimeReviewGrouping = "person" | "project" | "organization";

export type TimeReviewTicket = {
    key: TicketKey;
    title: string;
    seconds: number;
};

/** `seconds` is the part of the entry that landed in this group, not always its whole length. */
export type TimeReviewEntry = {
    id: WorkLogId;
    userId: UserId;
    userName: string;
    started: Date;
    stopped: Date;
    seconds: number;
    text: string | null;
    projectName: string | null;
    organizationName: string | null;
    tickets: TimeReviewTicket[];
};

/** A person, project, or organization. The all-zero id is time that reached no project or organization. */
export type TimeReviewGroup = {
    id: string;
    label: string;
    totalSeconds: number;
    ticketSeconds: number;
    projectSeconds: number;
    organizationSeconds: number;
    unattachedSeconds: number;
    tickets: TimeReviewLine[];
    projects: TimeReviewLine[];
    organizations: TimeReviewLine[];
    people: TimeReviewLine[];
    entries: TimeReviewEntry[];
};

export type TimeReview = {
    grouping: TimeReviewGrouping;
    totalSeconds: number;
    ticketSeconds: number;
    projectSeconds: number;
    organizationSeconds: number;
    unattachedSeconds: number;
    groups: TimeReviewGroup[];
};

export type TimeReviewFilter = {
    users?: UserId[] | undefined;
    projects?: ProjectId[] | undefined;
    organizations?: OrganizationId[] | undefined;
    groupBy?: TimeReviewGrouping | undefined;
};

type TimeReviewEntryResponse = Omit<TimeReviewEntry, "started" | "stopped"> & {
    started: string;
    stopped: string;
};

type TimeReviewGroupResponse = Omit<TimeReviewGroup, "entries"> & {
    entries: TimeReviewEntryResponse[];
};

type TimeReviewResponse = Omit<TimeReview, "grouping" | "groups"> & {
    grouping: number;
    groups: TimeReviewGroupResponse[];
};

const groupings: TimeReviewGrouping[] = ["person", "project", "organization"];

/** A pay period covers [from, to): `to` is the first day of the next one. Dates are calendar days. */
export type PayPeriodRange = {
    from: string;
    to: string;
};

export type PayPeriod = {
    /** False while periods still fall back to the default Monday anchor. */
    configured: boolean;
    days: number;
    current: PayPeriodRange;
    previous: PayPeriodRange;
};

export type WorkLogApi = {
    getReview(from: Date, to: Date, filter?: TimeReviewFilter): Promise<TimeReview>;
    /** `on` is the caller's calendar day, so a period boundary lands on the user's midnight. */
    getPayPeriod(on: string): Promise<PayPeriod>;
    getWorkLogs(from?: Date, to?: Date): Promise<WorkLogs>;
    /** One entry in full, yours or one you may manage. */
    get(id: WorkLogId): Promise<WorkLog>;
    getDeleted(): Promise<DeletedWorkLog[]>;
    recover(workLogs: WorkLogId[]): Promise<number>;
    start(
        text: string | null,
        tickets?: WorkLogTicketAllocation[],
        projectId?: ProjectId | null,
        organizationId?: OrganizationId | null
    ): Promise<WorkLog>;
    log(
        text: string | null,
        started: Date,
        stopped: Date,
        tickets?: WorkLogTicketAllocation[],
        projectId?: ProjectId | null,
        organizationId?: OrganizationId | null
    ): Promise<WorkLog>;
    stop(id: WorkLogId): Promise<WorkLog>;
    stopAll(): Promise<number>;
    update(id: WorkLogId, edit: WorkLogEdit): Promise<WorkLog>;
    remove(id: WorkLogId): Promise<void>;
};

export const workLogApi: WorkLogApi = {
    async getReview(from, to, filter) {
        const { data } = await axios.get<TimeReviewResponse>("/api/v0/workLogs/review", {
            params: {
                from: from.toISOString(),
                to: to.toISOString(),
                users: filter?.users,
                projects: filter?.projects,
                organizations: filter?.organizations,
                groupBy: filter?.groupBy,
            },
            // Repeated keys (users=a&users=b) are what the server binds an array from.
            paramsSerializer: { indexes: null },
        });

        return {
            ...data,
            grouping: groupings[data.grouping] ?? "person",
            groups: data.groups.map((group) => ({
                ...group,
                entries: group.entries.map((entry) => ({
                    ...entry,
                    started: new Date(entry.started),
                    stopped: new Date(entry.stopped),
                })),
            })),
        };
    },

    async getPayPeriod(on) {
        const { data } = await axios.get<PayPeriod>("/api/v0/workLogs/payPeriod", {
            params: { on },
        });

        return data;
    },

    async getWorkLogs(from, to) {
        const { data } = await axios.get<{
            entries: WorkLogResponse[];
            serverNow: string;
            requireTarget: boolean;
        }>("/api/v0/workLogs", {
            params: {
                from: from?.toISOString(),
                to: to?.toISOString(),
            },
        });

        return {
            entries: data.entries.map(toWorkLog),
            serverNow: new Date(data.serverNow),
            requireTarget: data.requireTarget,
        };
    },

    async get(id) {
        const { data } = await axios.get<WorkLogResponse>(`/api/v0/workLogs/${id}`);

        return toWorkLog(data);
    },

    async getDeleted() {
        const { data } = await axios.get<DeletedWorkLogResponse[]>("/api/v0/workLogs/deleted");

        return data.map((entry) => ({
            ...entry,
            started: new Date(entry.started),
            stopped: entry.stopped === null ? null : new Date(entry.stopped),
            deletedAt: entry.deletedAt === null ? null : new Date(entry.deletedAt),
        }));
    },

    async recover(workLogs) {
        const { data } = await axios.post<{ recovered: number }>("/api/v0/workLogs/recover", {
            workLogs,
        });

        return data.recovered;
    },

    async start(text, tickets, projectId, organizationId) {
        const { data } = await axios.post<WorkLogResponse>("/api/v0/workLogs", {
            text,
            tickets: tickets ?? [],
            projectId: projectId ?? null,
            organizationId: organizationId ?? null,
        });

        return toWorkLog(data);
    },

    async stop(id) {
        const { data } = await axios.post<WorkLogResponse>(`/api/v0/workLogs/${id}/stop`);

        return toWorkLog(data);
    },

    async stopAll() {
        const { data } = await axios.post<{ stopped: number }>("/api/v0/workLogs/stopAll");

        return data.stopped;
    },

    async log(text, started, stopped, tickets, projectId, organizationId) {
        const { data } = await axios.post<WorkLogResponse>("/api/v0/workLogs", {
            text,
            started: started.toISOString(),
            stopped: stopped.toISOString(),
            tickets: tickets ?? [],
            projectId: projectId ?? null,
            organizationId: organizationId ?? null,
        });

        return toWorkLog(data);
    },

    async update(id, edit) {
        const { data } = await axios.put<WorkLogResponse>(`/api/v0/workLogs/${id}`, {
            text: edit.text,
            started: edit.started.toISOString(),
            stopped: edit.stopped?.toISOString() ?? null,
            tickets: edit.tickets,
            projectId: edit.projectId,
            organizationId: edit.organizationId,
        });

        return toWorkLog(data);
    },

    async remove(id) {
        await axios.delete(`/api/v0/workLogs/${id}`);
    },
};
